import { FlareXMLDeclaration } from '@common/flare/constants/flare-xml-declaration.constant';
import { ToFlareXMLOptions } from '@common/flare/types/to-flare-xml-options.type';
import { defaultToFlareXML } from '@common/flare/util/prosemirror-flare-xml';
import { NodeIdAttrName, NodeIdRootValue } from '@common/prosemirror/model/node-id';
import { SchemaPlugin } from '@common/prosemirror/model/schema-plugin';
import { OperationAttrName } from '@common/prosemirror/operations/operations.constant';
import { getSchemaAttrs } from '@common/util/schema';
import { NodeSpec, ProseMirrorNode } from 'prosemirror-model';

/** Destination XML node names. */
export enum DestinationXmlNodeName {
  Destination = 'CatapultDestination',
}

/** Destination tag XML node attribute names. */
export enum DestinationTagXmlAttributeName {
  Version = 'Version',
  Comment = 'Comment',
  Type = 'Type',
  Host = 'Host',
  Port = 'Port',
  ConnectionMode = 'ConnectionMode',
  Directory = 'Directory',
  AnonymousLogin = 'AnonymousLogin',
  ViewUrl = 'ViewUrl',
  UploadLogFile = 'UploadLogFile',
  RemoveStale = 'RemoveStale',
  ChangedOnly = 'ChangedOnly',
  SftpServerValidation = 'SftpServerValidation',
  SftpHostFile = 'SftpHostFile',
  SftpAuthentication = 'SftpAuthentication',
  SftpPrivateKeyFile = 'SftpPrivateKeyFile',
  SftpPrivateKeyPassword = 'SftpPrivateKeyPassword',
  BasicLogin = 'BasicLogin',
  ClickUrl = 'ClickUrl',
  DocumentName = 'DocumentName',
  DocumentDescription = 'DocumentDescription',
  Folder = 'Folder',
}

export class DestinationSchemaPlugin extends SchemaPlugin {
  nodes: Dictionary<NodeSpec> = {
    doc: {
      content: DestinationXmlNodeName.Destination,
      toFlareXML(node: ProseMirrorNode, options: ToFlareXMLOptions): string {
        let xml = FlareXMLDeclaration;

        for (let i = 0; i < node.childCount; i += 1) {
          const child = node.child(i);
          xml += (child.type.spec.toFlareXML ?? defaultToFlareXML)(child, options)
        }

        return xml;
      }
    },

    [DestinationXmlNodeName.Destination]: {
      content: 'block*',
      attrs: {
        [NodeIdAttrName]: { default: NodeIdRootValue, skipExport: true },
        [OperationAttrName]: { default: undefined, skipExport: true },
        [DestinationTagXmlAttributeName.Version]: { default: undefined },
        [DestinationTagXmlAttributeName.Comment]: { default: undefined },
        [DestinationTagXmlAttributeName.Type]: { default: undefined },
        [DestinationTagXmlAttributeName.Host]: { default: undefined },
        [DestinationTagXmlAttributeName.Port]: { default: undefined },
        [DestinationTagXmlAttributeName.ConnectionMode]: { default: undefined },
        [DestinationTagXmlAttributeName.Directory]: { default: undefined },
        [DestinationTagXmlAttributeName.AnonymousLogin]: { default: undefined },
        [DestinationTagXmlAttributeName.ViewUrl]: { default: undefined },
        [DestinationTagXmlAttributeName.UploadLogFile]: { default: undefined },
        [DestinationTagXmlAttributeName.RemoveStale]: { default: undefined },
        [DestinationTagXmlAttributeName.ChangedOnly]: { default: undefined },
        [DestinationTagXmlAttributeName.SftpServerValidation]: { default: undefined },
        [DestinationTagXmlAttributeName.SftpHostFile]: { default: undefined },
        [DestinationTagXmlAttributeName.SftpAuthentication]: { default: undefined },
        [DestinationTagXmlAttributeName.SftpPrivateKeyFile]: { default: undefined },
        [DestinationTagXmlAttributeName.SftpPrivateKeyPassword]: { default: undefined },
        [DestinationTagXmlAttributeName.BasicLogin]: { default: undefined },
        [DestinationTagXmlAttributeName.ClickUrl]: { default: undefined },
        [DestinationTagXmlAttributeName.DocumentName]: { default: undefined },
        [DestinationTagXmlAttributeName.DocumentDescription]: { default: undefined },
        [DestinationTagXmlAttributeName.Folder]: { default: undefined },
      },
      parseDOM: [{
        tag: DestinationXmlNodeName.Destination,
        getAttrs(dom: HTMLElement) {
          return getSchemaAttrs(dom, [
            DestinationTagXmlAttributeName.Version,
            DestinationTagXmlAttributeName.Comment,
            DestinationTagXmlAttributeName.Type,
            DestinationTagXmlAttributeName.Host,
            DestinationTagXmlAttributeName.Port,
            DestinationTagXmlAttributeName.ConnectionMode,
            DestinationTagXmlAttributeName.Directory,
            DestinationTagXmlAttributeName.AnonymousLogin,
            DestinationTagXmlAttributeName.ViewUrl,
            DestinationTagXmlAttributeName.UploadLogFile,
            DestinationTagXmlAttributeName.RemoveStale,
            DestinationTagXmlAttributeName.ChangedOnly,
            DestinationTagXmlAttributeName.SftpServerValidation,
            DestinationTagXmlAttributeName.SftpHostFile,
            DestinationTagXmlAttributeName.SftpAuthentication,
            DestinationTagXmlAttributeName.SftpPrivateKeyFile,
            DestinationTagXmlAttributeName.SftpPrivateKeyPassword,
            DestinationTagXmlAttributeName.BasicLogin,
            DestinationTagXmlAttributeName.ClickUrl,
            DestinationTagXmlAttributeName.DocumentName,
            DestinationTagXmlAttributeName.DocumentDescription,
            DestinationTagXmlAttributeName.Folder,
          ]);
        }
      }],
    },
  };
}
